<template>
    <div>
        <v-view :tableData="tableData"
                :tableProps="tableProps"
                :modalProps="modalProps"
                :columns="columns"
                :routes="routes"
                :buttonsVisibility="buttonsVisibility"
                :initialSort="initialSort"
                :rowButtonAction="rowButtonAction"
                :rowButtonFunctions="rowButtonFunctions"
                :renderColumnAction="renderColumnAction"
                :showModeOpenId="showModeOpenId"
                :viewParams="viewParams"
                v-model:selectedRows="selectedRows">

            <template #crud="formProps">
                <StatementForm :formProps="formProps">
                </StatementForm>
            </template>
            <template #search="searchProps">
                <StatementSearch :searchProps="searchProps">
                </StatementSearch>
            </template>

            <template #buttons="formProps">
                <div>
                    <CustomButton name="bank-data-import"
                                  @click.stop="() => bankDataImport(formProps)"
                                  :className="buttonClass('teal')">
                        <template class="flex items-center gap-1"><i class="fa-solid fa-file-arrow-down"></i><span>{{ __('Bank data import') }}</span></template>
                    </CustomButton>
                </div>
                <div>
                    <BackButton v-if="isBackButtonVisible"
                                routeName="operation-index"
                                :customData="{ 'id': viewParams.operation_id }" />
                </div>

                <div style="margin-top:-20px;margin-left:50px;">
                    <div style="font-size:11px;margin-left:7px;color:#1A83AD;font-weight:bold;font-style:italic">{{ __('Bank account') }}:</div>
                    <div>
                        <FormSelect fieldName="company_bank_account"
                                    label=""
                                    showClear
                                    style="width:250px;padding-top:0"
                                    :filtering="false"
                                    :isFormDisabled="false"
                                    :change="(e) => filterStatementsByAccountNumber(e, formProps)"
                                    :options="select?.accounts"
                                    v-model:field="companyBankAccountToFilterStatements" />
                    </div>
                </div>
                <div style="margin-top:-20px">
                    <div style="font-size:11px;margin-left:7px;color:#1A83AD;font-weight:bold;font-style:italic">{{ __('Operation type') }}:</div>
                    <div>
                        <FormSelect fieldName="operation_type"
                                    label=""
                                    showClear
                                    style="width:250px;padding-top:0"
                                    :filtering="false"
                                    :isFormDisabled="false"
                                    :change="(e) => filterStatementsByOperationType(e, formProps)"
                                    :options="select?.bank_operation_types"
                                    v-model:field="bankOperationTypeToFilterStatements" />
                    </div>
                </div>
            </template>
        </v-view>

        <Dialog modal
                v-model:visible="isBankDataImportModalOpen"
                :header="__('Bank data import')"
                :unstyled="false"
                :style="{ width: '500px' }"
                :pt="dialogPt">
            <p style="font-size:16px; font-weight:bold;color:cadetblue">{{ __('Data will be imported from XML file') }}</p>

            <BankDataImportForm :formProps="bankDataImportFormProps"
                                v-model:isBankDataImportModalOpen="isBankDataImportModalOpen">
            </BankDataImportForm>
        </Dialog>

        <Dialog modal
                v-model:visible="isQuoteAssignModalOpen"
                :header="__('Quote assigning')"
                :unstyled="false"
                :style="{ width: '40%' }"
                :pt="dialogPt">
            <p style="font-size:16px; font-weight:bold;color:cadetblue">{{ __('Quote will be assigned to selected project') }}</p>

            <QuoteToProjectAssignForm :formData="quoteAssignFormData"
                                      v-model:isQuoteAssignModalOpen="isQuoteAssignModalOpen">
            </QuoteToProjectAssignForm>
        </Dialog>

    </div>
</template>

<script setup>
import { ref, onBeforeMount } from "vue";
import StatementForm from './StatementForm.vue'
import StatementSearch from './StatementSearch.vue'
import BankDataImportForm from './BankDataImportForm.vue'
import Dialog from 'primevue/dialog';
import FormSelect from '@crud/FormSelect.vue'
import CustomButton from '@crud/CustomButton.vue'
import BackButton from '@crud/BackButton.vue'
import QuoteToProjectAssignForm from './QuoteToProjectAssignForm.vue'
import { rowButtonAction, prepareSelect } from '@pages/Helper/crud_utils.js'
import { __, getFromUrl, buttonClass, getFromRoute } from '@pages/Helper/utils.js';

const props = defineProps({
    tableData: Object,
    tableProps: Object,
    modalProps: Object,
    columns: Object,
    routes: Object,
    initialSort: Object,
    buttonsVisibility: Object,
    filters: Object,
    viewParams: Object,
})

const selectedRows = ref([])
const showModeOpenId = getFromUrl()
const isBankDataImportModalOpen = ref(false)
const isQuoteAssignModalOpen = ref(false)
const bankDataImportFormProps = ref(null)
const quoteAssignFormData = ref(null)
const companyBankAccountToFilterStatements = ref(props.filters?.company_bank_account || null)
const bankOperationTypeToFilterStatements = ref(props.filters?.operation_type || null)
const options = ['accounts', 'bank_operation_types']
const select = ref(null)
const isBackButtonVisible = ref(false)

const dialogPt = {
    header: { class: 'form-header' },
    content: { class: 'form-body' },
};

//------------------------------------------------------------------------------

onBeforeMount(async () => {
    select.value = await prepareSelect(options)
    if (props.viewParams.operation_id) {
        isBackButtonVisible.value = true
    }
})

//------------------------------------------------------------------------------

const bankDataImport = (formProps) => {
    bankDataImportFormProps.value = formProps
    isBankDataImportModalOpen.value = true
}

//------------------------------------------------------------------------------

const filterStatementsByAccountNumber = async (e, formProps) => {
    const bankAccount = e.value
    await getFromRoute('statement-filter-by-account-number', { company_bank_account: bankAccount }, 'get')
    formProps.getLazyData({ page: 1 })
}

//------------------------------------------------------------------------------

const filterStatementsByOperationType = async (e, formProps) => {
    const operationType = e.value
    await getFromRoute('statement-filter-by-operation-type', { operation_type: operationType }, 'get')
    formProps.getLazyData({ page: 1 })
}

//------------------------------------------------------------------------------

const renderColumnAction = (item, rowData, $column) => {
    if (item.id == "project-statements") {
        if (rowData.action_button_state === 'disabled') {
            return { disabled: true, 'style': item.style + ';opacity: 0.3;', 'event': null, tooltip: __('Quote was already assigned') };
        } else if (rowData.action_button_state === 'hidden') {
            return { 'name': '', 'style': '', 'event': null };
        }
    }
}

//------------------------------------------------------------------------------

const rowButtonFunctions = {
    click: (data) => {
        quoteAssignFormData.value = data
        isQuoteAssignModalOpen.value = true
    }
}

//------------------------------------------------------------------------------

</script>

<style lang="scss" scoped></style>
